import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchLockdowns, getLockdownDetail, getFilterMatrix } from '../requests/api';

const getData = (res) => res?.data ?? res;

export const loadLockdowns = createAsyncThunk(
	'lockdown/search',
	async (params, { rejectWithValue }) => {
		try { return getData(await searchLockdowns(params)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const loadLockdownDetail = createAsyncThunk(
	'lockdown/detail',
	async (id, { rejectWithValue }) => {
		try { return getData(await getLockdownDetail(id)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

// Loads the FULL matrix of filter combinations for a tech.
// Called once when a tech is selected or when dates change.
// The frontend then cascades locally without further backend calls.
export const loadFilterMatrix = createAsyncThunk(
	'lockdown/filterMatrix',
	async ({ id, tech, dateFrom, dateTo }, { rejectWithValue }) => {
		try {
			const data = getData(await getFilterMatrix(id, tech, dateFrom, dateTo));
			// Normalise keys to camelCase in case backend returns PascalCase
			const rows = (Array.isArray(data) ? data : []).map(r => ({
				jvId:      r.jvId      ?? r.JvId      ?? null,
				cellName:  r.cellName  ?? r.CellName  ?? null,
				side:      r.side      ?? r.Side      ?? null,
				polygon:   r.polygon   ?? r.Polygon   ?? null,
				vendor:    r.vendor    ?? r.Vendor     ?? null,
				manager:   r.manager   ?? r.Manager    ?? null,
				optimiser: r.optimiser ?? r.Optimiser  ?? null,
			}));
			return { tech, rows };
		}
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

const lockdownSlice = createSlice({
	name: 'lockdown',
	initialState: {
		list:           [],
		listLoading:    false,
		listError:      null,
		selected:       null,
		detailLoading:  false,
		detailError:    null,

		// Raw combination rows per tech — used for frontend cascade.
		// matrixByTech['5G'] = [ { jvId, cellName, side, polygon, vendor, manager, optimiser }, ... ]
		matrixByTech:        {},
		matrixLoadingByTech: {},
	},
	reducers: {
		clearSelected(state) { state.selected = null; },
	},
	extraReducers: b => {
		b.addCase(loadLockdowns.pending,   s => { s.listLoading = true;  s.listError = null; })
			.addCase(loadLockdowns.fulfilled, (s, a) => { s.listLoading = false; s.list = a.payload; })
			.addCase(loadLockdowns.rejected,  (s, a) => { s.listLoading = false; s.listError = a.payload; });

		b.addCase(loadLockdownDetail.pending,   s => { s.detailLoading = true; s.detailError = null; })
			.addCase(loadLockdownDetail.fulfilled, (s, a) => { s.detailLoading = false; s.selected = a.payload; })
			.addCase(loadLockdownDetail.rejected,  (s, a) => { s.detailLoading = false; s.detailError = a.payload; });

		b.addCase(loadFilterMatrix.pending,   (s, a) => {
			const tech = a.meta.arg.tech;
			if (tech) s.matrixLoadingByTech[tech] = true;
		})
			.addCase(loadFilterMatrix.fulfilled, (s, a) => {
				const { tech, rows } = a.payload;
				if (tech) {
					s.matrixLoadingByTech[tech] = false;
					s.matrixByTech[tech] = rows;
				}
			})
			.addCase(loadFilterMatrix.rejected,  (s, a) => {
				const tech = a.meta.arg.tech;
				if (tech) s.matrixLoadingByTech[tech] = false;
			});
	},
});

export const { clearSelected } = lockdownSlice.actions;
export default lockdownSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectMatrixForTech = (tech) => (state) =>
	state.lockdown.matrixByTech[tech] ?? [];

export const selectMatrixLoadingForTech = (tech) => (state) =>
	!!state.lockdown.matrixLoadingByTech?.[tech];